import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import "./css/Login.css";

import uaeFlag from "../assets/icons/country_code.png";
import logo from "../assets/images/logo.png";

export default function Login() {
  const [phone, setPhone] = useState("");
  const [policy, setPolicy] = useState(false);
  const [touched, setTouched] = useState(false);
  const [throwError, setThrowError] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const phoneRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication status
    const authStatus = localStorage.getItem("isAuthenticated") === "true";

    // Redirect if authenticated
    if (authStatus) {
      navigate("/profile-step-1", { replace: true });
    }
  }, [navigate]);

  const AUTH_BASE_URL = process.env.REACT_APP_AUTH_BASE_URL;
  const VERSION = process.env.REACT_APP_VERSION;
  const language = localStorage.getItem("app_language") || "en";
  const isArabic = language === "ar";

  const texts = {
    en: {
      title: "Let’s get you onboard!",
      subtitle: "Enter your mobile number",
      invalid: "Please input correct number to proceed",
      invalidPhone: "Please enter a valid UAE mobile number (e.g. 50xxxxxxx)",
      acceptPolicy: "Please accept the policy to continue",
      tos: "By continuing, I accept the",
      terms: "terms of service",
      and: "and",
      privacy: "privacy policy",
      continue: "Continue",
      otpSent: "The OTP has been sent successfully!",
      otpError: "Error sending OTP",
      otpFail: "Failed to send OTP. Try again.",
    },
    ar: {
      title: "لنبدأ رحلتك معنا!",
      subtitle: "أدخل رقم هاتفك المحمول",
      invalid: "الرجاء إدخال رقم صحيح للمتابعة",
      invalidPhone: "الرجاء إدخال رقم إماراتي صالح (مثل 50xxxxxxx)",
      acceptPolicy: "الرجاء قبول الشروط للمتابعة",
      tos: "بالمتابعة، أوافق على",
      terms: "شروط الخدمة",
      and: "و",
      privacy: "سياسة الخصوصية",
      continue: "متابعة",
      otpSent: "تم إرسال رمز التحقق بنجاح!",
      otpError: "خطأ في إرسال رمز التحقق",
      otpFail: "فشل في إرسال رمز التحقق، حاول مرة أخرى",
    },
  };

  const t = texts[isArabic ? "ar" : "en"];

  const handleContinue = async () => {
    setFormLoading(true);
    if (/^\d{9}$/.test(phone) && policy) {
      setThrowError(false);
      try {
        const response = await fetch(`${AUTH_BASE_URL}/${VERSION}/contractor/login`, {
          method: "POST",
          body: JSON.stringify({ phone: "971" + phone }),
          headers: { "Content-Type": "application/json" },
        });

        const data = await response.json();
        setFormLoading(false);
        if (data.response_code === 200) {
          showToast(t.otpSent, "success");
          navigate("/otp", { state: { phone } });
        } else {
          showToast(data.message || t.otpError, "error");
        }
      } catch (error) {
        setFormLoading(false);
        showToast(t.otpFail, "error");
      }
    } else {
      setThrowError(true);
      setFormLoading(false);
    }
  };

  return (
    <div className={`common-wrapper ${isArabic ? "rtl" : ""}`}>
      <div className="login-content">
        {/* Logo */}
        <div className="logo">
          <img src={logo} alt="Fixora Logo" />
        </div>

        {/* Title */}
        <h2 className="title">{t.title}</h2>
        <p className="subtitle">{t.subtitle}</p>

        {/* Input Field */}
        <div
          className={`input-container ${(touched || throwError) && phone.length !== 9 ? "error" : ""}`}
        >
          <img className="country-code" src={uaeFlag} alt="UAE" />
          <input
            ref={phoneRef}
            type="text"
            maxLength={9}
            inputMode="numeric"
            placeholder="50 XXX XXXX"
            value={phone}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              const isValidStart = /^(50|52|54|55|56|58)/.test(value);

              if (/^[0-9]*$/.test(value) && value.length <= 9) {
                setPhone(value);

                if (value.length === 9) {
                  phoneRef.current?.blur();
                  if (!isValidStart) {
                    showToast(t.invalidPhone, "error");
                    setPhone("");
                  }
                }
              }

              if (!touched) setTouched(true);
            }}
          />
        </div>

        {(touched || throwError) && phone.length !== 9 && (
          <div className="error">{t.invalid}</div>
        )}
      </div>

      <div>{/* Policy */}
        <div className="outer-policy">
          <div className="policy">
            <input
              type="checkbox"
              checked={policy}
              onChange={(e) => setPolicy(e.target.checked)}
            />
            <p>
              {t.tos}{" "}
              <a href="https://g9eduinternational.com/terms-conditions/">{t.terms}</a>{" "}
              {t.and}{" "}
              <a href="https://g9eduinternational.com/privacy-policy/">{t.privacy}</a>
            </p>
          </div>
          {(touched || throwError) && !policy && (
            <div className="error">{t.acceptPolicy}</div>
          )}
        </div>

        {/* Continue Button */}
        <button onClick={handleContinue} className="continue-btn" disabled={formLoading}>
          {formLoading ? <span className="loader"></span> : t.continue}
        </button>
      </div>
    </div>
  );
}